import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import {
    getHomePath,
    getCategoryPath,
    getPdpPath,
    getPaymentPath,
    getOrderSuccessPath,
    getOrderFailurePath,
    getOrderCancellationPath,
    getErrorNoRetryPath,
    getCartPath,
    getConfirmationPath,
    getResumePath,
    getUnsubscribePath
} from './paths';
import Navigate from './Navigate';
import ErrorBoundary from '../error/ErrorBoundary';
import Step from './Step';
import { getEnv, getIsOldUiPreferred } from '../app/bootstrap';

import {
    HOME_STEP,
    CATEGORY_STEP,
    PDP_STEP,
    PAYMENT,
    ORDER_SUCCESS,
    ORDER_FAILURE,
    ORDER_CANCELLATION,
    CART_STEP,
    CONFIRMATION_STEP, PAGE_TITLE_MESSAGE_IDS
} from './flowConstants';
import { FlowProvider } from './FlowProvider';
import { GifPlaybackProvider } from '../common/GifPlaybackContext';
import HomePage from '../landingPage/LandingPage';
import HomePageV2 from '../landingPage_v2/LandingPage';
import HomePageV3 from '../landingPage_v2/LandingPageNew';
import CategoryPage from '../landingPage/CategoryPage/CategoryPage';
import NotFound from '../error/NotFound';
import ErrorNoRetry from '../error/ErrorNoRetry';
import Loader from '../loader/loader';
import { getIsGridViewHomePageEnabled } from '../session/sessionSelectors';


const ProductDetailsPage = lazy(() => import('../giftCardDetailsPage/ProductDetailsPage'));
const ProductDetailsPageNew = lazy(() => import('../giftCardDetailsPage_v2/ProductDetailsPage'));
const CartPage = lazy(() => import('../cart/Cart'));
const ConfirmationPage = lazy(() => import('../confirmation/paymentConfirmation'));
const OrderSuccess = lazy(() => import('../order/OrderSuccess'));
const OrderFailure = lazy(() => import('../order/OrderFailure'));
const PaymentPageV1 = lazy(() => import('../payment/PaymentPage'));
const PaymentPageV2 = lazy(() => import('../payment_v2/PaymentPage'));
const PaymentPlusRedirectHandler = lazy(() => import('../payment/paymentPlusRedirectHandler'));
const CartResumePage = lazy(() => import('../cartResume/CartResumePage'));
const UnsubscribePage = lazy(() => import('../unsubscribe/UnsubscribePage'));
const OrderCancellationSection = lazy(() => import('../orderCancellation/cancellationSection'));

export const FlowRoute = () => {
    const homePath = getHomePath();
    const navigateToHome = <Navigate pathname={homePath} />;
    const skipCart = getIsOldUiPreferred();
    const location = useLocation();

    
    const isGridViewHomePageEnabled = useSelector(
        state => (state ? getIsGridViewHomePageEnabled(state) : false)
    );
    const renderNewHomePage = isGridViewHomePageEnabled ? HomePageV3 : HomePageV2;

    
    useEffect(() => {
        const root = document.getElementById('app-root');
        if (root) {
            if (!root.hasAttribute('tabindex')) {
                root.setAttribute('tabindex', '-1');
            }
            root.focus();
        }
    }, [location.pathname]);

    return (
        <ErrorBoundary>
            <GifPlaybackProvider>
                <FlowProvider>
                    <Suspense fallback={<Loader showTestEnv={getEnv() !== 'PROD'} />}>
                        <Routes>
                            <Route key="root" path="/" element={navigateToHome} />
                            <Route
                                key={HOME_STEP}
                                path={getHomePath()}
                                element={(
                                    <Step
                                        stepName={HOME_STEP}
                                        component={getIsOldUiPreferred()
                                            ? HomePage : renderNewHomePage}
                                        pageTitle={PAGE_TITLE_MESSAGE_IDS[HOME_STEP]}
                                    />
                                )}
                            />
                            <Route
                                key={CATEGORY_STEP}
                                path={getCategoryPath()}
                                element={(
                                    <Step
                                        stepName={CATEGORY_STEP}
                                        component={CategoryPage}
                                        pageTitle={PAGE_TITLE_MESSAGE_IDS[CATEGORY_STEP]}
                                    />
                                )}
                            />
                            <Route
                                key={PDP_STEP}
                                path={getPdpPath()}
                                element={(
                                    <Step
                                        stepName={PDP_STEP}
                                        component={getIsOldUiPreferred() ? ProductDetailsPage : ProductDetailsPageNew}
                                        hasDependencies
                                        pageTitle={PAGE_TITLE_MESSAGE_IDS[PDP_STEP]}
                                    />
                                )}
                            />
                            <Route
                                key={CART_STEP}
                                path={getCartPath()}
                                element={!skipCart ? (
                                    <Step
                                        stepName={CART_STEP}
                                        component={CartPage}
                                        hasDependencies
                                        pageTitle={PAGE_TITLE_MESSAGE_IDS[CART_STEP]}
                                    />
                                )
                                    : (<NotFound errorCode={404} />)
                                }
                            />
                            <Route
                                key={CONFIRMATION_STEP}
                                path={getConfirmationPath()}
                                element={(
                                    <Step
                                        stepName={CONFIRMATION_STEP}
                                        component={ConfirmationPage}
                                        pageTitle={PAGE_TITLE_MESSAGE_IDS[CONFIRMATION_STEP]}
                                    />
                                )}
                            />
                            <Route
                                key={PAYMENT}
                                path={getPaymentPath()}
                                element={getIsOldUiPreferred()
                                    ? <Step stepName={PAYMENT} component={PaymentPageV1} />
                                    : (
                                        <Step
                                            stepName={PAYMENT}
                                            component={PaymentPageV2}
                                            hasDependencies
                                            pageTitle={PAGE_TITLE_MESSAGE_IDS[PAYMENT]}
                                        />
                                    )
                                }
                            />
                            <Route
                                key={ORDER_SUCCESS}
                                path={getOrderSuccessPath()}
                                element={(
                                    <Step
                                        stepName={ORDER_SUCCESS}
                                        component={OrderSuccess}
                                        pageTitle={PAGE_TITLE_MESSAGE_IDS[ORDER_SUCCESS]}
                                    />
                                )}
                            />
                            <Route
                                key={ORDER_FAILURE}
                                path={getOrderFailurePath()}
                                element={(
                                    <Step
                                        stepName={ORDER_FAILURE}
                                        component={OrderFailure}
                                        pageTitle={PAGE_TITLE_MESSAGE_IDS[ORDER_FAILURE]}
                                    />
                                )}
                            />
                            <Route
                                key={ORDER_CANCELLATION}
                                path={getOrderCancellationPath()}
                                element={(
                                    <Step
                                        stepName={ORDER_CANCELLATION}
                                        component={OrderCancellationSection}
                                        pageTitle={PAGE_TITLE_MESSAGE_IDS[ORDER_CANCELLATION]}
                                    />
                                )}
                            />
                            <Route
                                key="redirect-order-handler-confirmation"
                                path="/storefront/paymentConfirmation"
                                element={<PaymentPlusRedirectHandler stepName="redirection-flow-order" />}
                            />
                            <Route
                                key="redirect-order-handler-failed"
                                path="/storefront/paymentFailed"
                                element={<PaymentPlusRedirectHandler stepName="redirection-flow-order" />}
                            />
                            <Route
                                key="cart-resume"
                                path={getResumePath()}
                                element={<CartResumePage />}
                            />
                            <Route
                                key="unsubscribe"
                                path={getUnsubscribePath()}
                                element={<UnsubscribePage />}
                            />
                            <Route
                                key="error-no-retry"
                                path={getErrorNoRetryPath()}
                                element={<ErrorNoRetry />}
                            />
                            <Route path="*" key="page-not-found" element={<NotFound errorCode={404} />} />
                        </Routes>
                    </Suspense>
                </FlowProvider>
            </GifPlaybackProvider>
        </ErrorBoundary>
    );
};

export default FlowRoute;
